import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { REALMS, type SkillProblem, type ProblemState } from '../data/realms';
import { problemsBySkillId } from '../data/problems';
// Broad union (incl. ReactProblem) — SkillModal lists React problems too, which
// the IDE-runnable `Problem` from '../data/problems' intentionally excludes.
import type { Problem } from '../data/problems/_types';
import Fizzbuzz from './Fizzbuzz';
import { getSolvedSet, isAttempted } from '../lib/progress';
import { useFocusTrap } from '../lib/useFocusTrap';

const DIFF_COLOR: Record<string, string> = { easy: 'cream', med: 'amber', hard: 'coral' };

function diffOf(p: Problem): SkillProblem['diff'] {
  return p.difficulty === 'Easy' ? 'easy' : p.difficulty === 'Medium' ? 'med' : 'hard';
}

// Mirrors CategoryList's starter shape so isAttempted compares against the
// same canonical text the IDE saves on edit.
function starterFiles(p: Problem): Array<{ name: string; code: string; readonly?: boolean; isEntry?: boolean }> {
  const bundle = p.files;
  // ReactProblem.files only carries `javascript`; algo/class carry python too;
  // fullstack makes python/javascript optional; agent carries python only —
  // coalesce to [] in every case.
  const py = (bundle && 'python' in bundle ? bundle.python : []) ?? [];
  const js = (bundle && 'javascript' in bundle ? bundle.javascript : []) ?? [];
  if (py.length || js.length) {
    return [...py, ...js].map(f => ({ name: f.name, code: f.code, readonly: f.readonly, isEntry: f.isEntry }));
  }
  // pyStarter / jsStarter exist only on algorithm/class problems.
  const pyStarter = 'pyStarter' in p ? p.pyStarter ?? '' : '';
  const jsStarter = 'jsStarter' in p ? p.jsStarter ?? '' : '';
  return [
    { name: 'solution.py', code: pyStarter, isEntry: true },
    { name: 'solution.js', code: jsStarter, isEntry: true },
  ];
}

function deriveState(p: Problem, solved: Set<string>): ProblemState {
  if (solved.has(p.id)) return 'done';
  if (isAttempted(p.id, starterFiles(p))) return 'attempted';
  return 'todo';
}

function toSkillProblem(p: Problem, n: number, state: ProblemState): SkillProblem {
  return {
    n,
    name: p.title,
    diff: diffOf(p),
    state,
    time: `~${p.estTimeMin ?? 20}m`,
    tags: (p.tags && p.tags.length > 0) ? p.tags.join(', ') : p.skillId,
    problemId: p.id,
  };
}

// Includes React problems (jsx-1, etc.) — they're in the bank with a skillId
// and route to ReactProblemPane via CodeCompiler. Previously this listed only
// algorithm + class problems, so React skills showed an empty problem list
// even though their badge counted 1.
function problemsForSkill(skillId: string, solved: Set<string>): SkillProblem[] {
  return problemsBySkillId(skillId)
    .map((p, i) => toSkillProblem(p, i + 1, deriveState(p, solved)));
}

interface Props {
  skillId: string;
  onClose: () => void;
}

const SkillModal: React.FC<Props> = ({ skillId, onClose }) => {
  const navigate = useNavigate();

  const context = useMemo(() => {
    for (const realm of REALMS) {
      const skill = realm.skills.find(s => s.id === skillId);
      if (skill) return { realm, skill };
    }
    return null;
  }, [skillId]);

  const problems = useMemo<SkillProblem[]>(
    () => problemsForSkill(skillId, getSolvedSet()),
    [skillId],
  );

  // Index of the row currently highlighted via keyboard. Starts at the first
  // non-locked row so Enter is meaningful out of the gate.
  const [focusIdx, setFocusIdx] = useState(() =>
    Math.max(0, problems.findIndex(p => p.state !== 'locked')),
  );

  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, true);
  const listRef = useRef<HTMLDivElement>(null);

  // Arrow-key navigation. Esc is handled by the parent (IslandMap's stack
  // controller) so a single source of truth pops modals one level at a time.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowDown' || e.key === 'j') {
        setFocusIdx(i => {
          let n = i;
          for (let step = 0; step < problems.length; step++) {
            n = (n + 1) % problems.length;
            if (problems[n].state !== 'locked') break;
          }
          return n;
        });
        e.preventDefault();
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        setFocusIdx(i => {
          let n = i;
          for (let step = 0; step < problems.length; step++) {
            n = (n - 1 + problems.length) % problems.length;
            if (problems[n].state !== 'locked') break;
          }
          return n;
        });
        e.preventDefault();
      } else if (e.key === 'Enter' || e.key === ' ') {
        const p = problems[focusIdx];
        if (p && p.state !== 'locked' && p.problemId) {
          navigate(`/problem/${p.problemId}`);
          e.preventDefault();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [problems, focusIdx, navigate]);

  // Keep the focused row in view as the user arrow-keys through a long list.
  useEffect(() => {
    const container = listRef.current;
    if (!container) return;
    const row = container.querySelector<HTMLElement>(`[data-row-idx="${focusIdx}"]`);
    if (row) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusIdx]);

  if (!context) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(30,27,22,0.55)', zIndex: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          className="sk-box shadow"
          style={{ padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, background: 'var(--paper)' }}
        >
          <Fizzbuzz size={80} mood="thinking" />
          <div style={{ fontWeight: 700, fontSize: 17 }}>Skill not found</div>
          <button className="sk-btn primary" onClick={onClose}>← back</button>
        </div>
      </div>
    );
  }

  const { realm, skill } = context;
  const solved = problems.filter(p => p.state === 'done').length;
  const attempted = problems.filter(p => p.state === 'attempted').length;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${skill.name} — ${skill.pun}`}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(30, 27, 22, 0.6)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: 'sb-modal-fade 0.18s ease-out',
      }}
    >
      <div
        ref={dialogRef}
        onClick={e => e.stopPropagation()}
        className="sk-box shadow"
        style={{
          background: 'var(--paper-2)',
          width: 'min(92vw, 760px)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'sb-modal-rise 0.22s cubic-bezier(0.2, 0.7, 0.3, 1.2)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: '14px 18px',
            borderBottom: '1.4px dashed var(--ink)',
            background: 'var(--paper)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="mono-cap">
              {realm.name.toLowerCase()} › {skill.name.toLowerCase()}
            </span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="sk-btn ghost"
              style={{ fontSize: 18, padding: '2px 10px' }}
            >
              ✕
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>{skill.name}</h2>
            <span style={{ fontStyle: 'italic', color: 'var(--ink-soft)', fontSize: 13 }}>"{skill.pun}"</span>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
            <span className="sk-pill amber">{solved} / {problems.length} solved</span>
            {attempted > 0 && (
              <span className="sk-pill peach" title="started but not passing">
                {attempted} in progress
              </span>
            )}
            <span style={{ flex: 1 }} />
            <span className="mono-cap">
              ↑↓ to move · enter to solve · esc to back
            </span>
          </div>
        </div>

        {/* Problem list */}
        <div ref={listRef} className="scroll-y" style={{ flex: 1, padding: 12 }}>
          {problems.map((p, idx) => (
            <ProblemRow
              key={p.n}
              p={p}
              focused={idx === focusIdx}
              dataIdx={idx}
              onClick={() => {
                if (p.state === 'locked' || !p.problemId) return;
                setFocusIdx(idx);
                navigate(`/problem/${p.problemId}`);
              }}
              onHover={() => setFocusIdx(idx)}
            />
          ))}

          {problems.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-faint)' }}>
              <Fizzbuzz size={64} mood="thinking" />
              <div style={{ marginTop: 12 }}>No problems in this skill yet.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface ProblemRowProps {
  p: SkillProblem;
  focused: boolean;
  dataIdx: number;
  onClick: () => void;
  onHover: () => void;
}

const ProblemRow: React.FC<ProblemRowProps> = ({ p, focused, dataIdx, onClick, onHover }) => {
  const isLocked = p.state === 'locked';
  const isDone = p.state === 'done';
  const isAttempt = p.state === 'attempted';

  const circleBackground = isDone
    ? 'var(--w-amber)'
    : isAttempt
      ? 'linear-gradient(to top, var(--w-amber) 0%, var(--w-amber) 50%, var(--paper) 50%, var(--paper) 100%)'
      : 'var(--paper)';

  return (
    <div
      data-row-idx={dataIdx}
      className="sk-box tight"
      onClick={onClick}
      onMouseEnter={onHover}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        marginBottom: 6,
        background: focused && !isLocked ? 'var(--w-cream)' : isLocked ? 'transparent' : 'var(--paper)',
        backgroundImage: isLocked
          ? 'repeating-linear-gradient(45deg, transparent 0 6px, rgba(30,27,22,0.18) 6px 7px)'
          : 'none',
        opacity: isLocked ? 0.7 : 1,
        cursor: isLocked ? 'default' : 'pointer',
        outline: focused && !isLocked ? '2px solid var(--w-coral)' : 'none',
        outlineOffset: -2,
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          border: '1.5px solid var(--ink)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 700,
          background: circleBackground,
          flexShrink: 0,
        }}
      >
        {isDone ? '✓' : isLocked ? '🔒' : p.n}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.name}
        </div>
        <div className="mono-cap">
          {p.tags} · {p.time}{p.gate ? ` · ${p.gate}` : ''}
        </div>
      </div>

      <span className={`sk-pill ${DIFF_COLOR[p.diff] ?? ''}`} style={{ fontSize: 10 }}>
        {p.diff}
      </span>

      {!isLocked && <span style={{ fontSize: 14, color: 'var(--ink-faint)' }}>→</span>}
    </div>
  );
};

export default SkillModal;
